import { useState, useEffect } from "react";
import { Modal, Button } from "react-bootstrap";
import toast from "react-hot-toast";
import { liquidarOperacionFactoring } from "../../utils/factoring";
import { useCurrency } from "../../utils/useCurrency";

const formatMontoDisplay = (value, pais) => {
  if (!value && value !== 0) return "";
  if (typeof value === "string") return value;
  const locale = pais === "Chile" ? "es-CL" : "es-PE";
  return value.toLocaleString(locale, { maximumFractionDigits: 0 });
};

const parseMontoInput = (displayValue, moneda) => {
  const clean = displayValue.replace(/[^\d,.]/g, "");
  if (!clean) return "";
  const lastDot = clean.lastIndexOf(".");
  const lastComma = clean.lastIndexOf(",");
  let numStr;
  if (lastDot >= 0 && lastComma >= 0) {
    numStr = lastDot > lastComma ? clean.replace(/,/g, "") : clean.replace(/\./g, "").replace(",", ".");
  } else if (lastComma >= 0) {
    numStr = clean.replace(",", ".");
  } else if (lastDot >= 0) {
    numStr = moneda === "CLP" || moneda === "PEN" ? clean.replace(/\./g, "") : clean;
  } else {
    numStr = clean;
  }
  if (numStr.endsWith(".")) return numStr;
  const num = parseFloat(numStr);
  return isNaN(num) ? "" : num;
};

const LiquidarFactoringModal = ({ isOpen, onClose, operacion, pais, monedaPrincipal, onSuccess }) => {
  const { formatAmount } = useCurrency();

  const [fechaPagoReal, setFechaPagoReal] = useState("");
  const [montoRemanente, setMontoRemanente] = useState("");
  const [loading, setLoading] = useState(false);

  const remanenteEsperado = Number(operacion?.monto_factura || 0) - Number(operacion?.capital_adelantado || 0);

  useEffect(() => {
    if (!isOpen || !operacion) return;
    const hoy = new Date().toISOString().split("T")[0];
    setFechaPagoReal(operacion.fecha_cobro_final || hoy);
    setMontoRemanente(Math.round(remanenteEsperado));
  }, [isOpen, operacion, remanenteEsperado]);

  const handleLiquidar = async () => {
    if (!fechaPagoReal) return toast.error("Ingresá la fecha de pago real");
    const montoNum = typeof montoRemanente === "string" ? 0 : montoRemanente;
    if (!montoNum || montoNum <= 0) {
      toast.error("Ingresá un monto remanente válido");
      return;
    }
    setLoading(true);
    try {
      await liquidarOperacionFactoring(operacion, {
        pais,
        fechaPagoReal,
        monedaPrincipal,
        montoRemanente: montoNum,
      });
      toast.success("Operación liquidada");
      onSuccess?.();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Error liquidando:", err);
      toast.error("Error al liquidar la operación");
    } finally {
      setLoading(false);
    }
  };

  if (!operacion) return null;

  return (
    <Modal show={isOpen} onHide={onClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: "0.95rem" }}>Liquidar — Folio {operacion.factura?.folio || "—"}</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
        <div
          style={{
            padding: "10px 12px",
            marginBottom: 16,
            borderRadius: 6,
            background: "#f8f9fa",
            border: "1px solid #dee2e6",
            fontSize: "0.85rem",
          }}
        >
          <div style={{ marginBottom: 4 }}>
            <strong>Cliente:</strong> {operacion.factura?.razon_social_receptor || "—"}
          </div>
          <div style={{ marginBottom: 4 }}>
            <strong>Monto factura:</strong> {formatAmount(operacion.monto_factura, monedaPrincipal)}
          </div>
          <div style={{ marginBottom: 4 }}>
            <strong>Capital adelantado:</strong> {formatAmount(operacion.capital_adelantado, monedaPrincipal)}
          </div>
          <div style={{ color: "#c0392b" }}>
            <strong>Costo financiero:</strong> {formatAmount(operacion.costo_financiero, monedaPrincipal)}
          </div>
        </div>

        <div style={{ display: "flex", gap: 12, marginBottom: 12 }}>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: "0.78rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
              Fecha pago real
            </label>
            <input
              type="date"
              className="form-control form-control-sm"
              value={fechaPagoReal}
              onChange={(e) => setFechaPagoReal(e.target.value)}
              disabled={loading}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: "0.78rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
              Monto remanente ({monedaPrincipal})
            </label>
            <input
              type="text"
              className="form-control form-control-sm"
              value={formatMontoDisplay(montoRemanente, pais)}
              onChange={(e) => setMontoRemanente(parseMontoInput(e.target.value, monedaPrincipal))}
              disabled={loading}
            />
            <div style={{ fontSize: "0.7rem", color: "#888", marginTop: 2 }}>
              Remanente esperado: {formatAmount(remanenteEsperado, monedaPrincipal)}
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" size="sm" onClick={onClose} disabled={loading}>
          Cancelar
        </Button>
        <Button variant="primary" size="sm" onClick={handleLiquidar} disabled={loading}>
          {loading ? "Liquidando..." : "Liquidar"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default LiquidarFactoringModal;
